import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Film, 
  Plus, 
  Trash2, 
  Upload,
  Clock,
  Video,
  BookOpen,
  FileText
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['financial', 'mental', 'physical', 'lifestyle'];
const mediaTypes = ['video', 'lecture', 'document'];

export default function AdminMedia() {
  const { currentTheme } = useTheme();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    media_type: '',
    duration: '',
    file: null,
    thumbnail: null
  });

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media`);
      setMedia(response.data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('media_type', formData.media_type);
      data.append('duration', formData.duration);
      data.append('file', formData.file);
      if (formData.thumbnail) {
        data.append('thumbnail', formData.thumbnail);
      }

      await axios.post(`${API}/admin/media`, data, { 
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Media uploaded successfully!');
      setShowDialog(false);
      setFormData({
        title: '',
        description: '',
        category: '',
        media_type: '',
        duration: '',
        file: null,
        thumbnail: null
      });
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;

    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, { withCredentials: true });
      toast.success('Media deleted successfully!');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'lecture': return BookOpen;
      case 'document': return FileText;
      default: return Film;
    }
  };

  return (
    <AdminLayout title="Media Management">
      <div className="flex justify-between items-center mb-6">
        <p className="font-body opacity-70">
          Manage videos, lectures, and documents for your media library.
        </p>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: currentTheme.primary }} data-testid="add-media-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Upload New Media</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Media title"
                  required
                  data-testid="media-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this media"
                  required
                  data-testid="media-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                  >
                    <SelectTrigger data-testid="media-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select 
                    value={formData.media_type} 
                    onValueChange={(v) => setFormData({ ...formData, media_type: v })}
                  >
                    <SelectTrigger data-testid="media-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaTypes.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Duration (optional)</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 45:00"
                  data-testid="media-duration-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Media File</Label>
                <Input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  accept="video/*,audio/*,.pdf,.doc,.docx"
                  required
                  data-testid="media-file-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Thumbnail (optional)</Label>
                <Input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, thumbnail: e.target.files[0] })}
                  accept="image/*"
                  data-testid="media-thumbnail-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{ backgroundColor: currentTheme.primary }}
                disabled={uploading}
                data-testid="upload-media-btn"
              >
                {uploading ? 'Uploading...' : 'Upload Media'}
                <Upload className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skeleton h-64 rounded-xl" />
          ))}
        </div>
      ) : media.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {media.map((item) => {
            const Icon = getMediaIcon(item.media_type);
            return (
              <div
                key={item.media_id}
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                data-testid={`media-item-${item.media_id}`}
              >
                <div className="relative aspect-video bg-gray-100">
                  {item.thumbnail_url ? (
                    <img
                      src={`${BACKEND_URL}${item.thumbnail_url}`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-12 h-12 opacity-30" />
                    </div>
                  )}
                  {item.duration && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {item.duration}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                      style={{ backgroundColor: `${currentTheme.primary}20`, color: currentTheme.primary }}
                    >
                      {item.category}
                    </span>
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                      style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                    >
                      {item.media_type}
                    </span>
                  </div>
                  <h3 className="font-body font-medium mb-1" style={{ color: currentTheme.foreground }}>
                    {item.title}
                  </h3>
                  <p className="font-body text-sm opacity-60 line-clamp-2 mb-3">
                    {item.description}
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(item.media_id)}
                    data-testid={`delete-media-${item.media_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <Film className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
            No media yet
          </h3>
          <p className="font-body opacity-60">
            Upload your first video, lecture, or document.
          </p>
        </div>
      )}
    </AdminLayout>
  );
}
